import type { ViewController } from '@presentation/shell/NexxusShell';
import { ActivationService } from '@infra/licence/ActivationService';
import { CabinetAccessService } from '@application/licence/CabinetAccessService';
import { assertActivationAllowed } from '@application/licence/ActivationInstallGuard';
import { DeactivateService } from '@infra/licence/DeactivateService';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { fetchAndApplyEntitlements } from '@application/licence/EntitlementFetchService';
import { runLicenceFileImport } from '@presentation/views/parametres/LicenceRenewalPanel';
import { LicenceRenewalService } from '@infra/licence/LicenceRenewalService';
import { OfflineLicenceImportService } from '@infra/licence/OfflineLicenceImportService';
import { LicenceStatusService } from '@infra/licence/LicenceStatusService';
import type { StatusCheckResult } from '@infra/licence/LicenceStatusService';
import {
  notifyLicenceUpdated,
  syncLicenceEntitlements,
} from '@application/licence/LicenceEntitlementService';
import { NetworkStatusService } from '@infra/network/NetworkStatusService';
import { ToastService } from '@presentation/components/Toast';
import { clearAuthStateForActivation } from '@application/auth/authBridge';

type Ctx = Parameters<ViewController>[1];

/** Message clair (jamais silencieux) pour le résultat d'un contrôle de statut forcé auprès du serveur. */
export function statusCheckMessage(result: StatusCheckResult): {
  level: 'success' | 'warning' | 'error';
  text: string;
} {
  switch (result) {
    case 'active':
      return { level: 'success', text: 'Licence confirmée active par le serveur Nexxus.' };
    case 'revoked':
      return {
        level: 'error',
        text: 'Le serveur Nexxus indique que la licence de ce poste est révoquée. Contactez Nexxus.',
      };
    case 'suspended':
      return {
        level: 'error',
        text: 'Le serveur Nexxus indique que la licence de ce poste est suspendue ou expirée. Contactez Nexxus.',
      };
    case 'refused':
      return {
        level: 'warning',
        text: 'Le serveur a refusé la vérification du statut de licence. Réessayez ou contactez Nexxus.',
      };
    case 'offline':
      return {
        level: 'warning',
        text: 'Connexion Internet requise pour vérifier la licence auprès du serveur Nexxus.',
      };
    case 'skipped':
      return {
        level: 'warning',
        text: 'Aucune licence à vérifier sur ce poste. Activez une clé commerciale.',
      };
    default:
      return {
        level: 'warning',
        text: 'Serveur de licences injoignable : vérifiez la connexion Internet puis réessayez.',
      };
  }
}

/**
 * Actions de l'écran Paramètres › Licence. Enregistrées AVANT tout chargement de panneau : un panneau qui échoue
 * ne doit jamais rendre les boutons « Activer / Renouveler » inertes.
 * `repaint` redessine le panneau licence et la grille de modules.
 */
export function registerLicenceActions(
  w: Record<string, unknown>,
  section: HTMLElement,
  ctx: Ctx,
  repaint: () => Promise<void>,
): void {
  w['activerLicence'] = async (rawKey: string): Promise<boolean> => {
    const key = rawKey?.trim();
    if (!key) {
      ToastService.warning('Saisissez une clé commerciale');
      return false;
    }
    const { setInstallDataConsent } = await import('@infra/licence/installConsentStore');
    const { INSTALL_DATA_POLICY_VERSION } = await import('@domain/legal/InstallDataPolicy');
    if (!setInstallDataConsent(INSTALL_DATA_POLICY_VERSION)) {
      ToastService.error('Impossible d’enregistrer le consentement à la politique de données.');
      return false;
    }
    const sessionBefore = ctx.auth.getSession();
    const result = await new ActivationService().activate(key, (claims) =>
      assertActivationAllowed(ctx, claims),
    );
    if (!result.ok) {
      ToastService.error(result.error ?? 'Activation échouée');
      return false;
    }
    // Ajout d'une entreprise par sa clé : l'utilisateur qui la détient reçoit l'accès à ce dossier.
    if (sessionBefore) {
      const addedTenant = await SecureLicenceStore.getActiveTenantId();
      if (addedTenant && addedTenant !== sessionBefore.tenant_id) {
        await CabinetAccessService.grantAfterKeyActivation(ctx, sessionBefore.user_id, addedTenant);
      }
    }
    const runtime = ctx.getLicenceRuntime();
    try {
      if (runtime) {
        await runtime.loadFromStore();
        // La restriction (révoquée / suspendue) appartient à UNE entreprise : on relit celle de l'entreprise
        // qui vient d'être activée, sans garder l'état de la précédente.
        await LicenceStatusService.refreshForActiveTenant(runtime);
        await clearAuthStateForActivation(ctx);
        await fetchAndApplyEntitlements(ctx, runtime);
        await syncLicenceEntitlements(ctx, runtime);
        notifyLicenceUpdated();
      }
    } catch (err) {
      ToastService.error(err instanceof Error ? err.message : 'Erreur post-activation');
      return false;
    }
    ToastService.success('Licence activée — rechargement…');
    location.reload();
    return true;
  };

  // Bouton « Activer / Renouveler » : lit le champ de clé, active, puis redessine l'écran.
  w['activerLicenceDepuisChamp'] = async (inputId: string): Promise<void> => {
    const field = document.getElementById(inputId) as HTMLInputElement | null;
    const activated = await (w['activerLicence'] as (key: string) => Promise<boolean>)(
      field?.value ?? '',
    );
    if (activated) (w['renderParametres'] as (() => void) | undefined)?.();
  };

  // Bouton « Renouveler en ligne » : certificat et modules à jour, PUIS statut de licence relu auprès du serveur
  // (une licence réactivée côté serveur doit lever la lecture seule locale), avec un message dans tous les cas.
  w['renewLicenceNow'] = async (): Promise<void> => {
    const runtime = ctx.getLicenceRuntime();
    if (!runtime) {
      ToastService.warning('Licence active introuvable sur ce poste. Activez une clé commerciale.');
      return;
    }
    if (!NetworkStatusService.isBrowserOnline()) {
      ToastService.warning('Connexion Internet requise pour synchroniser la licence');
      return;
    }
    const synced = await LicenceRenewalService.syncEntitlementsFromServer(runtime, ctx, {
      force: true,
    });
    const status = await LicenceStatusService.checkStatus(runtime, { force: true });
    const message = statusCheckMessage(status);
    if (message.level === 'success' && synced) {
      ToastService.success('Licence synchronisée — le certificat a été mis à jour');
    } else if (message.level === 'success') {
      ToastService.success(message.text);
    } else if (message.level === 'error') {
      ToastService.error(message.text);
    } else {
      ToastService.warning(synced ? message.text : `Synchronisation impossible — ${message.text}`);
    }
    await repaint();
  };

  w['importLicenceFile'] = async (): Promise<void> => {
    await runLicenceFileImport(section, {
      importText: (text) =>
        OfflineLicenceImportService.importFromText(text, {
          beforePersist: (claims) => assertActivationAllowed(ctx, claims),
        }),
      afterImport: async () => {
        const runtime = ctx.getLicenceRuntime();
        if (runtime) {
          await runtime.loadFromStore();
          await LicenceStatusService.refreshForActiveTenant(runtime);
          await syncLicenceEntitlements(ctx, runtime);
          notifyLicenceUpdated();
        }
        await repaint();
      },
      notify: ToastService,
    });
  };

  w['deactivateLicenceDevice'] = async (): Promise<void> => {
    if (!confirm('Désactiver ce poste ? Une connexion Internet est requise.')) return;
    const res = await DeactivateService.deactivateCurrentDevice();
    if (!res.ok) {
      ToastService.error(res.error ?? 'Échec de la désactivation');
      return;
    }
    ToastService.success('Poste désactivé — redémarrage requis');
    location.reload();
  };
}
